// @ts-check
/**
 * Adapter between the DashboardAPI workflow detail response and the props the
 * flow detail components expect.
 *
 * The API groups linked tests by test type because that is how they are stored;
 * the detail view lists them as one table with a Type column, so the groups are
 * flattened here and the per-type counts kept separately for the coverage mix.
 *
 * @module businessViewDetailMapper
 */

/** @typedef {import('../shared/businessView.types').BusinessViewWorkflowDetailResponse} BusinessViewWorkflowDetailResponse */
/** @typedef {import('../shared/businessView.types').BusinessViewWorkflowDetail} BusinessViewWorkflowDetail */
/** @typedef {import('../shared/businessView.types').BusinessViewTestGroup} BusinessViewTestGroup */
/** @typedef {import('../shared/businessView.types').FlowLinkedTestRow} FlowLinkedTestRow */
/** @typedef {import('../shared/businessView.types').FlowResultBreakdown} FlowResultBreakdown */
/** @typedef {import('../shared/businessView.types').FlowDetailData} FlowDetailData */

import { getResultMeta, getResultRank, TEST_RESULT_ORDER, SHORT_TEST_TYPE_LABELS } from '../shared/businessViewConfig';

/** Days each exposure figure projects the daily risk cost over. */
export const EXPOSURE_DAYS = Object.freeze({ SHORT: 7, LONG: 30 });

/** An empty workflow, so the components never have to guard every field. */
const EMPTY_WORKFLOW = /** @type {BusinessViewWorkflowDetail} */ ({
  workflowId: 0,
  workflowKey: '',
  name: '',
  description: null,
  criticality: null,
  calculationStatus: null,
  calculationMessage: null,
  riskStatus: null,
  riskCategories: [],
  configuredCostImpact: 0,
  dailyRiskCost: null,
  totalTests: null,
  passedTests: null,
  nonPassingTests: null,
  passRate: null,
  affectedAreas: null,
  costImpactRank: 0,
  dailyRiskCostRank: 0,
  portfolioFlowCount: 0,
  calculatedAt: null,
});

/**
 * Flatten the API's test-type groups into table rows, carrying the group's type down.
 *
 * The key has to survive tests that share a name and have no id of their own: a not-found
 * test has neither sourceTestId nor resultItemId, so the group and position complete it.
 *
 * @param {BusinessViewTestGroup[]} [testGroups]
 * @returns {FlowLinkedTestRow[]}
 */
export const flattenTests = (testGroups = []) =>
  (testGroups ?? []).flatMap((group) =>
    (group?.tests ?? []).map((test, index) => {
      const { isPassing, isFailure } = getResultMeta(test?.result);

      return {
        ...test,
        key: `${group?.testTypeId ?? 'unknown'}-${test?.resultItemId ?? test?.sourceTestId ?? index}`,
        testTypeId: Number(group?.testTypeId),
        testType: group?.testType ?? '',
        testResultId: group?.testResultId ?? null,
        // A failure describes itself; anything else explains why it produced no verdict.
        description: test?.failureDescription ?? test?.resultMessage ?? null,
        isPassing,
        isFailure,
      };
    })
  );

/**
 * Worst result first, passes last, so what is wrong with the flow is at the top. The sort
 * is stable, so tests sharing a result keep the order the API sent them in.
 *
 * @param {FlowLinkedTestRow[]} [tests]
 * @returns {FlowLinkedTestRow[]}
 */
export const sortTestsByResult = (tests = []) =>
  [...(tests ?? [])].sort((a, b) => getResultRank(a?.result) - getResultRank(b?.result));

/**
 * Count the linked tests by result, in a fixed order, dropping results that did
 * not occur. Anything the API adds later sorts after the known results rather
 * than being discarded.
 *
 * @param {FlowLinkedTestRow[]} [tests]
 * @returns {FlowResultBreakdown[]}
 */
export const buildResultBreakdown = (tests = []) => {
  /** @type {Map<string, number>} */
  const counts = new Map();

  (tests ?? []).forEach((test) => {
    const result = String(test?.result ?? 'other');
    counts.set(result, (counts.get(result) ?? 0) + 1);
  });

  const rank = (/** @type {string} */ result) => {
    const index = TEST_RESULT_ORDER.indexOf(/** @type {any} */ (result));
    return index === -1 ? TEST_RESULT_ORDER.length : index;
  };

  return Array.from(counts.entries())
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([result, count]) => {
      const { label, dotClass, chartColour } = getResultMeta(result);
      return { result, label, count, dotClass, chartColour };
    });
};

/**
 * Per test type counts for the coverage mix ("3 UI · 2 API"). Falls back to the
 * display name the API sent when the type has no short label, so an unrecognised
 * test type still reads sensibly.
 *
 * @param {BusinessViewTestGroup[]} [testGroups]
 * @returns {{ testTypeId: number, label: string, count: number }[]}
 */
export const buildCoverageMix = (testGroups = []) =>
  (testGroups ?? []).map((group) => ({
    testTypeId: Number(group?.testTypeId),
    label: SHORT_TEST_TYPE_LABELS[Number(group?.testTypeId)] || group?.testType || 'Other',
    count: group?.count ?? group?.tests?.length ?? 0,
  }));

/**
 * Project a daily cost over a number of days. Null in, null out: a workflow with no
 * cost figure at all has no exposure, and £0 would read as a measured result rather
 * than an unknown one.
 *
 * @param {number|null|undefined} dailyCost
 * @param {number} days
 * @returns {number|null}
 */
export const projectExposure = (dailyCost, days) => (dailyCost == null ? null : dailyCost * days);

/**
 * The daily cost the detail view reports, and whether it is being incurred now.
 *
 * The figure is the *configured* impact - the standing per-day cost of this flow being
 * broken, which does not move with the run.
 *
 * Beware the name collision: the API's `dailyRiskCost` is what the flow is costing *right
 * now*, so it is 0 for anything not currently at risk. Reporting that as the daily cost
 * would give a healthy flow £0/day, reading as "worth nothing" rather than "fine". It is
 * used only to decide whether the money is being lost or merely at stake.
 *
 * @param {BusinessViewWorkflowDetail} workflow
 * @returns {{ dailyCost: number|null, hasCurrentRisk: boolean }}
 */
export const resolveDailyCost = (workflow) => {
  const dailyRiskCost = workflow?.dailyRiskCost ?? null;

  return {
    dailyCost: workflow?.configuredCostImpact ?? dailyRiskCost,
    hasCurrentRisk: (dailyRiskCost ?? 0) > 0,
  };
};

/**
 * Map a full detail response onto the payload the flow detail components consume.
 *
 * Counts are taken from the linked tests themselves rather than the stored
 * totals, so the donut, the legend and the table can never disagree. The stored
 * totals still drive the header, where they describe the calculation.
 *
 * @param {BusinessViewWorkflowDetailResponse|null} [response]
 * @returns {FlowDetailData}
 */
export const mapWorkflowDetail = (response) => {
  const workflow = { ...EMPTY_WORKFLOW, ...response?.workflow };
  const tests = sortTestsByResult(flattenTests(response?.testGroups));

  const passingCount = tests.filter((test) => test.isPassing).length;
  const { dailyCost, hasCurrentRisk } = resolveDailyCost(workflow);

  return {
    workflow,
    tests,
    breakdown: buildResultBreakdown(tests),
    coverageMix: buildCoverageMix(response?.testGroups),
    passingCount,
    nonPassingCount: tests.length - passingCount,
    passRate: workflow.passRate,
    isUnavailable: workflow.calculationStatus === 'unavailable' || workflow.riskStatus == null,
    isPartial: workflow.calculationStatus === 'partial',
    dailyCost,
    hasCurrentRisk,
    exposure7Day: projectExposure(dailyCost, EXPOSURE_DAYS.SHORT),
    exposure30Day: projectExposure(dailyCost, EXPOSURE_DAYS.LONG),
  };
};

export default mapWorkflowDetail;
